import React, { useState, useEffect } from 'react';
import Button from '../Button';
import Select from '../Select';
import Map from '../Map';
import { api, Parish } from '../../../services/api';

interface Location {
  parish: string;
  country: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
}

interface InlineEditLocationProps {
  value: Location | undefined;
  onSave: (value: Location) => Promise<void> | void;
  onCancel?: () => void;
  disabled?: boolean;
  className?: string;
  label?: string;
}

const InlineEditLocation: React.FC<InlineEditLocationProps> = ({
  value,
  onSave,
  onCancel,
  disabled = false,
  className = '',
  label,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [location, setLocation] = useState<Location>(
    value || { parish: '', country: 'Antigua and Barbuda', coordinates: { lat: 17.0747, lng: -61.7964 } }
  );
  const [parishes, setParishes] = useState<Parish[]>([]);
  const [isLoadingParishes, setIsLoadingParishes] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const loadParishes = async () => {
      try {
        const data = await api.parishes.getAll();
        setParishes(data);
      } catch (error) {
        console.error('Error loading parishes:', error);
      } finally {
        setIsLoadingParishes(false);
      }
    };
    loadParishes();
  }, []);

  const handleStartEdit = () => {
    if (disabled) return;
    setLocation(value || { parish: '', country: 'Antigua and Barbuda', coordinates: { lat: 17.0747, lng: -61.7964 } });
    setIsEditing(true);
  };

  const handleMapClick = async (lat: number, lng: number) => {
    setLocation((prev) => ({ 
      ...prev, 
      coordinates: { lat, lng },
      country: 'Antigua and Barbuda' // Auto-fill country
    }));
    try {
      const parish = await api.parishes.getByLocation(lat, lng);
      if (parish) {
        setLocation((prev) => ({ 
          ...prev, 
          parish: parish.name,
          country: 'Antigua and Barbuda' // Ensure country is set
        }));
      }
    } catch (error) {
      console.error('Error finding parish:', error);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(location);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving location:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setLocation(value || { parish: '', country: 'Antigua and Barbuda', coordinates: { lat: 17.0747, lng: -61.7964 } });
    setIsEditing(false);
    onCancel?.();
  };

  if (isEditing) {
    return (
      <div className={`inline-edit-wrapper ${className}`}>
        <div className="animate-slide-down space-y-4">
          {label && <label className="block text-sm font-medium text-gray-700">{label}</label>}
          <div className="grid grid-cols-2 gap-4">
            <Select
              value={location.parish}
              onChange={(e) => setLocation((prev) => ({ 
                ...prev, 
                parish: e.target.value,
                country: 'Antigua and Barbuda' // Auto-fill country when parish is selected
              }))}
              options={[
                { value: '', label: 'Select parish' },
                ...parishes.map((p) => ({ value: p.name, label: p.name })),
              ]}
              label="Parish"
              disabled={isLoadingParishes}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
              <input
                type="text"
                value={location.country}
                onChange={(e) => setLocation((prev) => ({ ...prev, country: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Antigua and Barbuda"
              />
            </div>
          </div>
          <div className="h-64 mb-4">
            <Map
              latitude={location.coordinates?.lat ?? 17.0747}
              longitude={location.coordinates?.lng ?? -61.7964}
              onMapClick={handleMapClick}
              markers={location.coordinates ? [{ lat: location.coordinates.lat, lng: location.coordinates.lng }] : []}
            />
          </div>
        </div>
        <div className="flex items-center gap-2 mt-4 animate-fade-in">
          <Button type="button" size="sm" onClick={handleSave} isLoading={isSaving} disabled={isSaving}>
            Save
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={handleCancel} disabled={isSaving}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`inline-edit-display group ${className}`}>
      <div className="inline-edit-content">
        {value ? (
          <div>
            <p className="font-medium">
              {value.parish}, {value.country}
            </p>
            {value.coordinates && (
              <p className="text-sm text-gray-500">
                {value.coordinates.lat.toFixed(4)}, {value.coordinates.lng.toFixed(4)}
              </p>
            )}
          </div>
        ) : (
          <span className="text-gray-400 italic">Not set</span>
        )}
      </div>
      {!disabled && (
        <button
          type="button"
          onClick={handleStartEdit}
          className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 ml-2"
          aria-label="Edit location"
        >
          <svg className="w-4 h-4 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default InlineEditLocation;

